import { Play, Video, ChevronRight, Star, Quote, TrendingUp } from 'lucide-react';

const SECRETOS_NAMES = {
  "Secreto #1": "Certificados de Viaje",
  "Secreto #2": "Asesorias Migratorias",
  "Secreto #3": "Plataformas B2B",
  "Secreto #4": "Club Vacacional Digital",
  "Secreto #5": "Embajador Digital",
  "Organizador": "Organizador de Tiempos"
};

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^&?#]+)/);
  return match ? match[1] : null;
}

export const TutorialsSection = ({ tutorials = [], variant = 'home' }) => {
  if (!tutorials || tutorials.length === 0) return null;

  const isEdu = variant === 'educacion';
  const openVideo = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  const groupedTutorials = tutorials.reduce((acc, tutorial) => {
    const key = tutorial.secreto;
    if (!acc[key]) acc[key] = [];
    acc[key].push(tutorial);
    return acc;
  }, {});

  return (
    <section data-testid="tutorials-section" className="mb-10">
      {/* Protagonist header band */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a0f1f] via-[#121a33] to-[#0a0f1f] border border-white/10 p-7 sm:p-10 mb-8">
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-[#80C753]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#45A0EE]/15 blur-3xl pointer-events-none" />
        <Quote className="absolute top-6 right-8 h-20 w-20 text-white/5 pointer-events-none" />
        <div className="relative">
          {isEdu ? (
            <>
              <div className="inline-flex items-center gap-2 bg-[#45A0EE]/15 border border-[#45A0EE]/40 rounded-full px-4 py-1.5 mb-4">
                <Video className="h-3.5 w-3.5 text-[#45A0EE]" />
                <span className="text-[#8cc5f5] text-[11px] font-black uppercase tracking-[2.5px]">Videos de formación</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.05] tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Aprende con nuestros <span className="bg-gradient-to-r from-[#45A0EE] via-[#80C753] to-[#E3C766] bg-clip-text text-transparent">videos de formación.</span>
              </h2>
              <p className="text-slate-300 text-sm sm:text-base max-w-2xl leading-relaxed">
                Clases y tutoriales grabados para que avances a tu ritmo. <span className="text-white font-semibold">Mira, aprende y aplica</span> cuando quieras.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 bg-[#80C753]/15 border border-[#80C753]/40 rounded-full px-4 py-1.5 mb-4">
                <Star className="h-3.5 w-3.5 text-[#80C753] fill-[#80C753]" />
                <span className="text-[#9BD872] text-[11px] font-black uppercase tracking-[2.5px]">Historias reales</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-[1.05] tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Agentes como tú,<br className="hidden sm:block" /> <span className="bg-gradient-to-r from-[#80C753] via-[#45A0EE] to-[#E3C766] bg-clip-text text-transparent">historias de éxito reales.</span>
              </h2>
              <p className="text-slate-300 text-sm sm:text-base max-w-2xl leading-relaxed">
                Cientos de personas ya cambiaron su vida con Club Viajemos. Estos son <span className="text-white font-semibold">algunos de nuestros protagonistas</span> — mira cómo lo lograron.
              </p>
              <div className="flex flex-wrap items-center gap-5 mt-5">
                <span className="inline-flex items-center gap-2 text-slate-200 text-sm font-semibold">
                  <TrendingUp className="h-4 w-4 text-[#80C753]" /> +5,600 agencias activas
                </span>
                <span className="inline-flex items-center gap-2 text-slate-200 text-sm font-semibold">
                  <Video className="h-4 w-4 text-[#45A0EE]" /> Cientos de casos de éxito
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="space-y-8">
        {Object.entries(groupedTutorials).map(([secreto, videos]) => (
          <div key={secreto}>
            <div className="flex items-center gap-2 mb-4 ml-1">
              <span className="text-sm font-bold text-[#45A0EE]">{secreto}</span>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-sm text-slate-500 dark:text-slate-400">{SECRETOS_NAMES[secreto] || secreto}</span>
              <span className="text-[10px] bg-slate-100 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full ml-1">
                {videos.length} {videos.length === 1 ? 'video' : 'videos'}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => {
                const ytId = getYouTubeId(video.url);
                const thumb = video.thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/mqdefault.jpg` : null);
                return (
                  <button
                    key={video.id}
                    onClick={() => openVideo(video.url)}
                    data-testid={`video-card-${video.id}`}
                    className="group relative bg-white dark:bg-[#141A29] border border-slate-200 dark:border-slate-700/60 rounded-2xl overflow-hidden hover:border-[#80C753]/60 hover:shadow-[0_22px_55px_-18px_rgba(128,199,83,0.35)] hover:-translate-y-1.5 transition-all duration-300 text-left"
                  >
                    {/* Thumbnail */}
                    <div className="relative w-full aspect-video bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 overflow-hidden">
                      {thumb ? (
                        <img
                          src={thumb}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                        </div>
                      )}
                      {/* success badge */}
                      <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 bg-[#80C753] text-[#06210f] text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shadow-lg">
                        <Star className="h-2.5 w-2.5 fill-[#06210f]" /> {isEdu ? 'Video' : 'Caso de éxito'}
                      </span>
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/0 to-black/0 group-hover:from-black/60 transition-colors flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-[#80C753] flex items-center justify-center shadow-xl opacity-95 group-hover:opacity-100 group-hover:scale-110 transition-all">
                          <Play className="h-6 w-6 text-[#06210f] ml-0.5 fill-[#06210f]" />
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-[#1D1D1B] dark:text-white leading-snug line-clamp-2 group-hover:text-[#3f8f1f] dark:group-hover:text-[#80C753] transition-colors">
                        {video.title}
                      </h3>
                      {video.description && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-1">
                          {video.description}
                        </p>
                      )}
                      <div className="flex items-center gap-1 mt-2.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-[#80C753] group-hover:gap-2 transition-all">
                        <Play className="h-3 w-3" />
                        <span>{isEdu ? 'Ver video' : 'Ver testimonio'}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default TutorialsSection;
